import type { AuthPermissionUser } from '../../services/authPermissions';

const CUSTOMER_ORDER_EDIT_ROLES = ['ADMIN', 'OPERATIONS', 'ORDER_STAFF', 'TESTER'];
const CUSTOMER_ORDER_OPERATION_ROLES = ['ADMIN', 'OPERATIONS'];
// Mirrors backend: ORDER_STAFF (Handlowiec) edits lines incl. sale prices and
// the commercial header of HIS OWN orders (ownership is backend-enforced).
const CUSTOMER_ORDER_PRODUCT_EDIT_ROLES = ['ADMIN', 'OPERATIONS', 'ORDER_STAFF'];
const CUSTOMER_ORDER_COMMERCIAL_EDIT_ROLES = ['ADMIN', 'OPERATIONS', 'ORDER_STAFF'];

export function isCustomerOrderTesterMode(user: AuthPermissionUser | null | undefined): boolean {
  return user?.role === 'TESTER';
}

export function canCreateCustomerOrders(user: AuthPermissionUser | null | undefined): boolean {
  return CUSTOMER_ORDER_EDIT_ROLES.includes(user?.role || '');
}

export function canEditCustomerOrderDetails(user: AuthPermissionUser | null | undefined): boolean {
  return CUSTOMER_ORDER_EDIT_ROLES.includes(user?.role || '');
}

export function canChangeCustomerOrderStatus(user: AuthPermissionUser | null | undefined): boolean {
  return CUSTOMER_ORDER_EDIT_ROLES.includes(user?.role || '');
}

export function canManageCustomerOrderOperations(user: AuthPermissionUser | null | undefined): boolean {
  return CUSTOMER_ORDER_OPERATION_ROLES.includes(user?.role || '');
}

/**
 * Sends the offer, the status and the confirmation e-mail to the customer from the ZK card: ADMIN (all), OPERATIONS
 * and the Handlowiec (his own ZK — ownership is backend-enforced). Owner
 * decision 2026-10-05. The offer lines with sale prices are put in by the
 * server at sending; Operacje preview them without prices. Backend gate:
 * `requireCustomerOrderEmailAccess` + the sales-rep allowlist.
 */
export function canSendCustomerEmail(user: AuthPermissionUser | null | undefined): boolean {
  return ['ADMIN', 'OPERATIONS', 'ORDER_STAFF'].includes(user?.role || '');
}

/**
 * The order PDF (offer: lines and sale prices, no cost or margin): readers of
 * sales documents and the Handlowiec on his own ZK (backend
 * `requireOrderPdfAccess`). Not Operacje — the PDF has sale prices.
 */
export function canDownloadCustomerOrderPdf(user: AuthPermissionUser | null | undefined, canViewSalesDocuments: boolean): boolean {
  return canViewSalesDocuments || user?.role === 'ORDER_STAFF';
}

export function canDeleteCustomerOrders(user: AuthPermissionUser | null | undefined): boolean {
  return CUSTOMER_ORDER_OPERATION_ROLES.includes(user?.role || '');
}

export function canEditCustomerOrderDocumentFields(user: AuthPermissionUser | null | undefined): boolean {
  return CUSTOMER_ORDER_OPERATION_ROLES.includes(user?.role || '');
}

export function canEditCustomerOrderProducts(user: AuthPermissionUser | null | undefined): boolean {
  return CUSTOMER_ORDER_PRODUCT_EDIT_ROLES.includes(user?.role || '');
}

/** Mirrors backend: draft editing stays available; later correction is ADMIN-only. */
export function canEditCustomerOrderDate(
  user: AuthPermissionUser | null | undefined,
  order?: { status?: string; commerciallyFrozenAt?: unknown } | null,
): boolean {
  return user?.role === 'ADMIN' || (['OPERATIONS', 'ORDER_STAFF'].includes(user?.role || '')
    && order?.status === 'NEW_INQUIRY' && !order.commerciallyFrozenAt);
}

export function canEditCustomerOrderCommercialFields(user: AuthPermissionUser | null | undefined): boolean {
  return CUSTOMER_ORDER_COMMERCIAL_EDIT_ROLES.includes(user?.role || '');
}

// Handlowiec (ORDER_STAFF) edits the rest of the commercial header on his own
// orders, but not the account manager (opiekun) — the owner decision of
// 2026-09-23 keeps order ownership reassignment out of his reach.
export function canEditCustomerOrderAccountManager(user: AuthPermissionUser | null | undefined): boolean {
  return canEditCustomerOrderCommercialFields(user) && user?.role !== 'ORDER_STAFF';
}

// WP13 (owner answer 6.9, 2026-09-24): ADMIN (Tomek, Emil) and OPERATIONS
// (Valentyna) change the forecast month; Handlowiec, TESTER and PARTNER only
// read it. Mirrors backend utils/orderPermissions.js.
export function canEditCustomerOrderForecastMonth(user: AuthPermissionUser | null | undefined): boolean {
  return CUSTOMER_ORDER_OPERATION_ROLES.includes(user?.role || '');
}

// Forecast by delivery (owner 2026-10-16): the „Transporty” table of the ZK
// card and its „Spodziewana data dostarczenia” — ADMIN, OPERATIONS and the
// Handlowiec on his own orders. Mirrors backend utils/orderPermissions.js.
export function canReadCustomerOrderDeliverySchedule(user: AuthPermissionUser | null | undefined): boolean {
  return CUSTOMER_ORDER_COMMERCIAL_EDIT_ROLES.includes(user?.role || '');
}

// WP14: "Dostawa" (our delivery / customer self-pickup) — ADMIN, OPERATIONS
// and the Handlowiec on his own orders; mirrors backend utils/orderPermissions.js.
export function canEditCustomerOrderDeliveryMode(user: AuthPermissionUser | null | undefined): boolean {
  return canEditCustomerOrderCommercialFields(user);
}

// „Edytuj pozycje” / „Koszty” in the ZK card header on every step after the
// Szkic: Emil and Tomek (ADMIN, owner 2026-10-06) and Valentyna (OPERATIONS,
// owner 2026-10-07; the line editor hides sale prices from her). Not the
// Handlowiec. The save rights themselves stay with
// canEditCustomerOrderProducts / the cost permissions.
export function canEditCustomerOrderOnAnyStep(user: AuthPermissionUser | null | undefined): boolean {
  return CUSTOMER_ORDER_OPERATION_ROLES.includes(user?.role || '');
}

export function canManageCustomerOrderSupplierLinks(user: AuthPermissionUser | null | undefined): boolean {
  return CUSTOMER_ORDER_OPERATION_ROLES.includes(user?.role || '');
}

export function canManageOrderNotificationAssignments(user: AuthPermissionUser | null | undefined): boolean {
  return user?.role === 'ADMIN';
}

export function canDeleteCustomerOrderTransportLink(user: AuthPermissionUser | null | undefined): boolean {
  return user?.role === 'ADMIN';
}

export function canCreatePartialSettlement(user: AuthPermissionUser | null | undefined): boolean {
  return user?.role === 'ADMIN' || user?.role === 'OPERATIONS';
}

export function canEnterSettlementFinancials(user: AuthPermissionUser | null | undefined): boolean {
  return user?.role === 'ADMIN';
}

export function canSettleCustomerOrder(user: AuthPermissionUser | null | undefined): boolean {
  return canCreatePartialSettlement(user);
}

export function canCloseCustomerOrderSettlement(user: AuthPermissionUser | null | undefined): boolean {
  return user?.role === 'ADMIN';
}
